import React from "react";
import { createProduct } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  LegacyForm,
  Modal,
  Space,
  useMessagedAsyncFn,
} from "@payping/web-components";
import type { ProductCreateViewModel, ProductDetailViewModel } from "@payping/api";

import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch } from "@/utils/redux";
import { Product } from "./type";
import { CuToman } from "@payping/icons";
import { numberFormatter } from "@payping/utilities-shared/number";

interface Props {
  visible: boolean;
  onCancel(): void;
  onOk(productData: Product): void;
}

const CreateProductModal: React.FC<Props> = ({ visible, onCancel, onOk }) => {
  const { t } = useAppTranslation();
  const [form] = LegacyForm.useForm();
  const dispatch = useAppDispatch();

  const [{ loading }, handleCreate] = useMessagedAsyncFn(
    async (productData: ProductDetailViewModel) => {
      const data: ProductCreateViewModel = {
        ...productData,
        amount: Number(productData.amount),
        // quantity: 1,
        unlimited: true,
        defineAmountByUser: false,
      };
      const productCode = await dispatch(createProduct(data));
      onOk({ code: productCode, ...data });
      return productCode;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch],
  );

  const footer = (
    <Space>
      <Button onClick={onCancel}>{t("cancel")}</Button>
      <Button loading={loading} onClick={form.submit} type={"primary"}>
        {t("product.create")}
      </Button>
    </Space>
  );

  return (
    <Modal open={visible} title={t("product.create")} onCancel={onCancel} footer={footer}>
      <LegacyForm
        onFinish={async (values) => {
          try {
            await handleCreate(values as ProductDetailViewModel);
          } catch {
            /* empty */
          }
        }}
        form={form}
        layout={"vertical"}
      >
        <LegacyForm.Item label={t("product.name")} rules={[{ required: true }]} name={"title"}>
          <Input />
        </LegacyForm.Item>
        <LegacyForm.Item label={t("product.description")} rules={[{ required: true }]} name={"description"}>
          <Input.TextArea />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={t("price")}
          rules={[
            { required: true },
            {
              validator: (_, value): Promise<void> =>
                value && !(Number(value) >= 1000)
                  ? Promise.reject(new Error(t("error.minimum", { quantity: 1000 })))
                  : Promise.resolve(),
            },
          ]}
          name={"amount"}
        >
          <InputNumber
            formatter={(value) => numberFormatter.format(value, { maxDecimals: 2 })}
            parser={(value) => numberFormatter.parse(value)}
            className="tw:w-full!"
            suffix={<CuToman width={24} height={24} />}
            inputMode="numeric"
            changeOnWheel={false}
            controls={false}
          />
        </LegacyForm.Item>

        <LegacyForm.Item name={"haveTax"} valuePropName={"checked"}>
          <Checkbox>{t("invoice.form.tax.label")}</Checkbox>
        </LegacyForm.Item>
      </LegacyForm>
    </Modal>
  );
};

export default CreateProductModal;
